import React from 'react';
import { Link } from 'react-router-dom';

export type DashStatTone = 'teal' | 'amber' | 'blue' | 'violet' | 'red' | 'slate';

interface DashStatBase {
  id: string;
  tone: DashStatTone;
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  /** When true, render a pulsing dot next to the value (used for
   *  "in-flight" docs > 0). */
  pulse?: boolean;
}

interface DashStatLink extends DashStatBase {
  to: string;
}

interface DashStatPlain extends DashStatBase {
  to?: never;
}

export type DashStat = DashStatLink | DashStatPlain;

interface DashboardStatsRowProps {
  stats: DashStat[];
}

const DashboardStatsRow: React.FC<DashboardStatsRowProps> = ({ stats }) => {
  return (
    <div className="dash-stats" role="list">
      {stats.map((s) => {
        const inner = (
          <>
            <div className={`dash-stat-icon dash-stat-icon--${s.tone}`} aria-hidden>
              {s.icon}
            </div>
            <div className="dash-stat-body">
              <div className="dash-stat-label">{s.label}</div>
              <div className="dash-stat-value-row">
                <span className="dash-stat-value">{s.value}</span>
                {s.pulse && <span className="dash-stat-pulse" aria-hidden />}
              </div>
              {s.hint && <div className="dash-stat-hint">{s.hint}</div>}
            </div>
          </>
        );
        const baseProps = {
          role: 'listitem' as const,
          className: `dash-stat dash-stat--${s.tone}${s.to ? ' dash-stat--link' : ''}`,
        };
        return s.to ? (
          <Link key={s.id} {...baseProps} to={s.to}>
            {inner}
          </Link>
        ) : (
          <div key={s.id} {...baseProps}>
            {inner}
          </div>
        );
      })}
    </div>
  );
};

export default DashboardStatsRow;
